import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTrialContext } from '../context/TrialContext';
import { trackQuizCompleted, trackPracticeMistakesOpened, trackQuizShared, trackPDFExported } from '../services/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuizResult = () => {
  const { quizId } = useParams();
  const location = useLocation();
  const [quiz, setQuiz] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPractice, setGeneratingPractice] = useState(false);
  const { token } = useAuth();
  const { trialEmail } = useTrialContext();
  const navigate = useNavigate();
  const userAnswers = location.state?.answers || {};
  const passedResult = location.state?.result;
  const isTrialUser = location.state?.isTrialUser || (!token && !!trialEmail);

  useEffect(() => {
    fetchQuizAndResult();
  }, []);

  // Track quiz completion when result is loaded
  useEffect(() => {
    if (result && quiz) {
      const userType = token ? 'premium' : (trialEmail ? 'trial' : 'free');
      trackQuizCompleted(quizId, quiz.questions?.length || 0, result.score || 0, userType);
    }
  }, [result, quiz, token, trialEmail, quizId]);

  const fetchQuizAndResult = async () => {
    try {
      // Fetch quiz
      const quizResponse = await axios.get(`${API_URL}/api/quiz/${quizId}`);
      setQuiz(quizResponse.data);

      // If result was passed from navigation state (trial users), use it
      if (passedResult) {
        setResult(passedResult);
      } else if (token) {
        // For authenticated users, fetch from history
        const historyResponse = await axios.get(
          `${API_URL}/api/quiz/history/me`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        // Find the most recent result for this quiz
        const quizResult = historyResponse.data.find(r => r.quiz_id === quizId);
        setResult(quizResult);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePracticeMistakes = async () => {
    try {
      setGeneratingPractice(true);
      console.log('Starting practice mistakes generation for quiz:', quizId);
      
      const response = await axios.post(
        `${API_URL}/api/quiz/practice-mistakes?quiz_id=${quizId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Practice quiz response:', response.data);
      const newQuizId = response.data.quiz_id;
      
      if (!newQuizId) {
        throw new Error('No quiz ID received from server');
      }
      
      console.log('Navigating to new quiz:', newQuizId);
      navigate(`/quiz/take/${newQuizId}`);
    } catch (error) {
      console.error('Failed to generate practice quiz:', error);
      
      let errorMessage = 'Failed to generate practice quiz.';
      if (error.response) {
        console.error('Error response:', error.response.data);
        errorMessage = error.response.data.detail || error.response.data.message || errorMessage;
      }
      
      alert(`❌ ${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
      setGeneratingPractice(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!quiz || !result) {
    return <div>No results found</div>;
  }

  const percentage = result.percentage;
  const passed = percentage >= 70;

  // Calculate incorrect answers
  const incorrectCount = result.total - result.score;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Quiz Results</h1>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Score Card */}
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center mb-8">
          <div className={`w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center ${
            passed ? 'bg-green-100' : 'bg-yellow-100'
          }`}>
            <span className="text-5xl">{passed ? '🏆' : '💪'}</span>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {passed ? 'Congratulations!' : 'Keep Studying!'}
          </h2>
          
          <div className="text-6xl font-bold text-indigo-600 my-6">
            {percentage.toFixed(1)}%
          </div>
          
          <p className="text-xl text-gray-600 mb-6">
            You scored {result.score} out of {result.total} questions correctly
          </p>

          {/* Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className={`h-4 rounded-full ${
                  passed ? 'bg-green-500' : 'bg-yellow-500'
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Detailed Review */}
        <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Question Review</h3>
          
          <div className="space-y-6">
            {quiz.questions.map((question, idx) => {
              const userAnswer = result.answers.find(a => a.question_id === question.id)?.user_answer;
              const isCorrect = userAnswer?.toLowerCase().trim() === 
                               question.correct_answer.toLowerCase().trim();

              return (
                <div
                  key={question.id}
                  className={`p-6 rounded-lg border-2 ${
                    isCorrect 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      {isCorrect ? (
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-sm font-medium text-gray-500 mr-2">Question {idx + 1}</span>
                        <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded capitalize">
                          {question.type.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <p className="text-lg font-medium text-gray-900 mb-3">{question.question}</p>
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Your answer: </span>
                          <span className={isCorrect ? 'text-green-700' : 'text-red-700'}>
                            {userAnswer || 'Not answered'}
                          </span>
                        </div>
                        
                        {!isCorrect && (
                          <div>
                            <span className="font-medium text-gray-700">Correct answer: </span>
                            <span className="text-green-700 font-medium">{question.correct_answer}</span>
                          </div>
                        )}
                        
                        <div className="pt-2 border-t border-gray-200">
                          <span className="font-medium text-gray-700">Explanation: </span>
                          <span className="text-gray-600">{question.explanation}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Practice My Mistakes - Only for authenticated users */}
        {incorrectCount > 0 && !isTrialUser && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-200 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Practice Your Mistakes
                </h3>
                <p className="text-gray-700 mb-4">
                  You got {incorrectCount} question{incorrectCount > 1 ? 's' : ''} wrong. 
                  Generate a new quiz focusing on these topics to improve your score!
                </p>
                <button
                  onClick={handlePracticeMistakes}
                  disabled={generatingPractice}
                  className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-medium hover:from-orange-700 hover:to-red-700 transition-all inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingPractice ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Generating Practice Quiz...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Practice My Mistakes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trial user upgrade prompt */}
        {isTrialUser && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">⭐</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  Enjoying Study Quiz?
                </h3>
                <p className="text-gray-700 mb-3">
                  Subscribe to unlock unlimited quizzes, quiz history, practice mistakes feature, and more!
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => navigate('/quiz/generate')}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            Generate New Quiz
          </button>
          <button
            onClick={() => navigate(isTrialUser ? '/' : '/dashboard')}
            className="px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-all"
          >
            {isTrialUser ? 'Back to Home' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizResult;
